import React, { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { Alert, Button, Spin, Typography } from "antd"
import { gen, swimminglyApi } from "./utils"
import EmailPreferencesPanel, { EmailPreference } from "./EmailPreferencesPanel"

const { Title, Paragraph, Text } = Typography

interface ApiResponse {
  status?: string
  message?: string
  preferences?: EmailPreference[]
  [key: string]: any
}

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

const EmailPreferencesPage: React.FC = () => {
  const query = useQuery()
  const token = query.get("token") || ""

  const [loading, setLoading] = useState<boolean>(true)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [preferences, setPreferences] = useState<EmailPreference[]>([])
  const [error, setError] = useState<string | null>(null)

  const loadPreferences = () => {
    if (!token) {
      setError(
        "This email preferences link is missing a token. Please use the link from your email.",
      )
      setPreferences([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    swimminglyApi
      .get(gen(`/api/public/emailPreferences?token=${encodeURIComponent(token)}`))
      .then((response: ApiResponse | undefined) => {
        if (!response) {
          setError("Unable to load email preferences.")
          setPreferences([])
          return
        }

        if (response.status !== "success") {
          setError(response.message || "Unable to load email preferences.")
          setPreferences([])
          return
        }

        if (Array.isArray(response.preferences)) {
          setPreferences(response.preferences)
        } else {
          setPreferences([])
        }
      })
      .catch(() => {
        setError("Unable to load email preferences.")
        setPreferences([])
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    loadPreferences()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const onTogglePreference = (pref: EmailPreference, isSubscribed: boolean) => {
    if (!token) return

    const previousValue = pref.isSubscribed

    setSavingKey(pref.channelKey)
    setPreferences((current) =>
      current.map((p) =>
        p.emailChannelId === pref.emailChannelId ? { ...p, isSubscribed } : p,
      ),
    )

    swimminglyApi
      .post(gen("/api/public/emailPreferences"))
      .body({ token, channelKey: pref.channelKey, isSubscribed })
      .then((response: ApiResponse | undefined) => {
        if (!response || response.status !== "success") {
          throw new Error(response?.message || "Unexpected response")
        }
      })
      .catch(() => {
        setError("Unable to update email preference. Please try again.")
        setPreferences((current) =>
          current.map((p) =>
            p.emailChannelId === pref.emailChannelId
              ? { ...p, isSubscribed: previousValue }
              : p,
          ),
        )
      })
      .finally(() => {
        setSavingKey(null)
      })
  }

  const hasPreferences = !loading && preferences.length > 0

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f7fafc",
        padding: "24px 16px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 640,
          background: "#ffffff",
          borderRadius: 8,
          boxShadow:
            "0 10px 15px -3px rgba(15, 23, 42, 0.08), 0 4px 6px -4px rgba(15, 23, 42, 0.06)",
          padding: "24px 24px 28px",
        }}
      >
        <Title level={3} style={{ marginBottom: 4 }}>
          Manage Your Email Preferences
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Use the switches below to choose which types of emails you receive
          from Swimmingly for this account.
        </Paragraph>

        {error && (
          <Alert
            type="error"
            title={error}
            style={{ marginBottom: 16 }}
            action={
              token && (
                <Button size="small" onClick={loadPreferences}>
                  Try again
                </Button>
              )
            }
          />
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <Spin />
          </div>
        ) : !hasPreferences ? (
          <Paragraph type="secondary" style={{ marginTop: 8 }}>
            No email preferences are available for this account yet.
          </Paragraph>
        ) : (
          <>
            <EmailPreferencesPanel
              preferences={preferences}
              readOnly={false}
              onToggle={(pref, isSubscribed) => {
                if (savingKey) return
                setSavingKey(pref.channelKey)
                onTogglePreference(pref, isSubscribed)
              }}
            />
          </>
        )}

        <Paragraph type="secondary" style={{ marginTop: 24, fontSize: 12 }}>
          If you did not request this email preferences link, you can safely
          ignore this page.
        </Paragraph>
      </div>
    </div>
  )
}

export default EmailPreferencesPage
